import type { InvoiceData } from "@/apis/gql/types";
import {
  type ApolloError,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  INVOICE_ACTIVITY_QUERY,
  LINE_ITEM_REVIEW_MODE_QUERY,
  UPDATE_INVOICES_STATUS_MUTATION,
  client,
} from "@/apis/gql/apolloClient";
import type { InvoiceType } from "@/lib/constants/invoice-type";
import type {
  InvoiceSmbReviewStatus,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";

import { LINE_ITEMS_QUERY } from "../line-items/fetch";
import { INVOICE_QUERY } from "./get";
import { INVOICES_QUERY, useGetAllInvoices } from "./get-all";
import { TABLE_INVOICE_FRAGMENT } from "./graphql";

export const useUpdateInvoicesStatus = () => {
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });

  const [updateInvoicesStatus] = useMutation(UPDATE_INVOICES_STATUS_MUTATION, {
    refetchQueries: [INVOICES_QUERY],
    update(cache, { data }, { variables }) {
      if (
        !data?.updateInvoicesStatus ||
        !variables?.invoiceIds ||
        !variables?.status
      )
        return;

      variables.invoiceIds.forEach((invoiceId: string) => {
        const cacheId = cache.identify({
          __typename: "Invoice",
          invoiceId,
        });

        const existing = cache.readFragment({
          id: cacheId,
          fragment: TABLE_INVOICE_FRAGMENT,
        });

        if (existing) {
          cache.writeFragment({
            id: cacheId,
            fragment: TABLE_INVOICE_FRAGMENT,
            data: {
              ...existing,
              status: variables.status,
            },
          });
        }
      });
    },
    optimisticResponse: ({ invoiceIds, status }) => ({
      updateInvoicesStatus: invoiceIds.map((invoiceId: string) => ({
        invoiceId,
        status,
        __typename: "InvoiceStatusUpdate",
      })),
    }),
  });

  return async (invoiceIds: string[], status: InvoiceStatus) => {
    const res = await updateInvoicesStatus({
      variables: { invoiceIds, status },
    });

    if (res.errors) {
      throw new Error("error updating invoices status");
    }

    await fetchInvoices({
      query: {
        invoiceId: {
          value: invoiceIds,
          op: "in",
        },
      },
    });

    return res.data;
  };
};

export const useUpdateInvoicesType = () => {
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });

  const [updateInvoicesType, { data, loading, error }] = useMutation(
    UPDATE_INVOICES_TYPE_MUTATION,
    {
      refetchQueries: [INVOICES_QUERY],
      update(cache, { data }, { variables }) {
        if (
          !data?.updateInvoicesType ||
          !variables?.invoiceIds ||
          !variables?.type
        )
          return;

        variables.invoiceIds.forEach((invoiceId: string) => {
          const cacheId = cache.identify({
            __typename: "Invoice",
            invoiceId,
          });

          const existing = cache.readFragment({
            id: cacheId,
            fragment: TABLE_INVOICE_FRAGMENT,
          });

          if (existing) {
            cache.writeFragment({
              id: cacheId,
              fragment: TABLE_INVOICE_FRAGMENT,
              data: {
                ...existing,
                type: variables.type,
              },
            });
          }
        });
      },
      optimisticResponse: ({ invoiceIds, type }) => ({
        updateInvoicesType: invoiceIds.map((invoiceId: string) => ({
          invoiceId,
          type,
          __typename: "InvoiceTypeUpdate",
        })),
      }),
    },
  );

  return [
    async (invoiceIds: string[], type: InvoiceType) => {
      const res = await updateInvoicesType({
        variables: { invoiceIds, type },
      });

      if (res.errors) {
        throw new Error("error updating invoices type");
      }

      await fetchInvoices({
        query: {
          invoiceId: {
            value: invoiceIds,
            op: "in",
          },
        },
      });

      return res.data;
    },
    { data, loading, error },
  ] as const;
};

export const UPDATE_INVOICES_TYPE_MUTATION = gql`
  mutation UpdateInvoicesType($invoiceIds: [String!]!, $type: String!) {
    updateInvoicesType(invoiceIds: $invoiceIds, type: $type) {
      invoiceId
      type
    }
  }
`;

type Update = {
  invoiceId: string;
  invoiceNo?: string | null;
  totalAmount?: number | null;
  currency?: string | null;
  invoiceDate?: Date | null;
  serviceDate?: Date | null;
  supplierId?: string | null;
  split?: boolean | null;
  isNettoInvoice?: boolean | null;
  dueDate?: Date | null;
  isBooking?: boolean | null;
  status?: InvoiceStatus | null;
  vatId?: string | null;
  manualProcessing?: boolean | null;
  isPaid?: boolean | null;
  smbReviewStatus?: InvoiceSmbReviewStatus | null;
  iban?: string | null;
  type?: InvoiceType | null;
};

export const useUpdateInvoice = (
  { config } = {
    config: {
      toasts: {
        success: true,
        loading: true,
      },
      refetchQueries: [
        LINE_ITEMS_QUERY,
        INVOICE_ACTIVITY_QUERY,
        LINE_ITEM_REVIEW_MODE_QUERY,
        INVOICE_QUERY,
      ],
    },
  },
): [
  (
    data: Update,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });
  const [updateInvoice, { data, loading, error }] = useMutation(
    UPDATE_INVOICE_MUTATION,
    {
      refetchQueries: [...config.refetchQueries, INVOICES_QUERY],
    },
  );

  const handleUpdate = async (
    data: Update,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId =
      config.toasts.loading && toast.loading(t("generic_loading"));

    try {
      const { invoiceId, ...rest } = data;

      const response = await updateInvoice({
        variables: { invoiceId, updatedFields: rest },
        optimisticResponse: ({ IGNORE }: any) => {
          const cache = client.readQuery({
            query: INVOICE_QUERY,
            variables: {
              invoiceId: invoiceId,
            },
          });

          if (!cache) {
            return IGNORE;
          }

          const invoiceDetails = cache.invoiceDetails;

          return {
            updateInvoice: {
              invoice: {
                __typename: "Invoice",
                ...invoiceDetails.invoice,
                ...data,
              },
              duplicates: invoiceDetails.duplicates,
              draftPayments: invoiceDetails.invoice.draftPayments,
            },
          };
        },
        update: (cache, { data }) => {
          const invoiceQuery = client.readQuery({
            query: INVOICE_QUERY,
            variables: {
              invoiceId: invoiceId,
            },
          });

          if (!invoiceQuery) {
            return;
          }

          const invoiceDetails = invoiceQuery.invoiceDetails;

          cache.writeQuery({
            query: INVOICE_QUERY,
            variables: {
              invoiceId: invoiceId,
            },
            data: {
              invoiceDetails: {
                ...invoiceDetails,
                duplicates: data.updateInvoice.duplicates,
                draftPayments: data.updateInvoice.draftPayments,
                invoice: {
                  ...data.updateInvoice.invoice,
                },
              },
            },
          });

          if (data.updateInvoice.invoice.supplierId !== undefined) {
            cache.evict({ fieldName: "invoices" });
            cache.gc();
          }
        },
      });

      if (response.errors) {
        throw new Error("error creating line item");
      }

      await fetchInvoices({
        query: {
          invoiceId: {
            value: [invoiceId],
            op: "in",
          },
        },
      });

      onSuccess();
      toastId && toast.dismiss(toastId);
      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId || undefined,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdate, { data, loading, error }];
};

export const UPDATE_INVOICE_MUTATION = gql`
  mutation UpdateInvoice($invoiceId: String!, $updatedFields: InvoiceInput!) {
    updateInvoice(invoiceId: $invoiceId, updatedFields: $updatedFields) {
      invoice {
        invoiceText
        aiSummary
        isNettoInvoice
        totalAmount
        totalAmountConfidence
        currency
        dueDate
        invoiceDate
        invoiceDateConfidence
        invoiceNoConfidence
        invoiceNo
        invoiceSource
        fileBlobName
        supplierId
        ocrReviewed
        status
        isNettoInvoice
        invoiceId
        createDatevPayment
        split
        reviewType
        serviceDate
        serviceDateConfidence
        isBooking
        vatId
        vatIdConfidence
        manualProcessing
        isPaid
        smbReviewStatus
        validationErrors
        bookingPeriod
        createdAt
        fileName
        isDuplicate
        processingStatus
        supplierMatchConfidence
        dueDateConfidence
        supplierMatch
        isDigitalInvoice
        type
      }
      draftPayments {
        draftPaymentId
        tenantId
        status
        amount
        rawAmount
        currency
        createdAt
        recipientName
        recipientIban
        purpose
        executionDate
        discount
      }
      duplicates {
        invoiceId
        status
        totalAmount
        invoiceNo
        createdAt
      }
    }
  }
`;

export const useUpdateInvoices = (
  { config } = {
    config: {
      toasts: {
        success: true,
        loading: true,
      },
    },
  },
): [
  (
    invoiceIds: string[],
    data: Omit<Update, "invoiceId">,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });
  const [updateInvoices, { data, loading, error }] = useMutation(
    UPDATE_INVOICES_MUTATION,
  );

  const handleUpdates = async (
    invoiceIds: string[],
    data: Omit<Update, "invoiceId">,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId =
      config.toasts.loading && toast.loading(t("generic_loading"));

    try {
      const response = await updateInvoices({
        variables: { invoiceIds, updatedFields: data },
      });

      if (response.errors) {
        throw new Error("error updating invoices");
      }

      await fetchInvoices({
        query: {
          invoiceId: {
            value: invoiceIds,
            op: "in",
          },
        },
      });

      onSuccess();
      toastId && toast.dismiss(toastId);
      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId || undefined,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdates, { data, loading, error }];
};

export const UPDATE_INVOICES_MUTATION = gql`
  mutation UpdateInvoices(
    $invoiceIds: [String!]!
    $updatedFields: InvoiceInput!
  ) {
    updateInvoices(invoiceIds: $invoiceIds, updatedFields: $updatedFields) {
      invoice {
        invoiceText
        aiSummary
        isNettoInvoice
        totalAmount
        totalAmountConfidence
        currency
        dueDate
        invoiceDate
        invoiceDateConfidence
        invoiceNoConfidence
        invoiceNo
        invoiceSource
        fileBlobName
        supplierId
        ocrReviewed
        status
        isNettoInvoice
        invoiceId
        createDatevPayment
        split
        reviewType
        serviceDate
        serviceDateConfidence
        isBooking
        vatId
        vatIdConfidence
        manualProcessing
        isPaid
        smbReviewStatus
        validationErrors
        bookingPeriod
        createdAt
        fileName
        isDuplicate
        processingStatus
        supplierMatchConfidence
        dueDateConfidence
        supplierMatch
        isDigitalInvoice
        type
      }
      duplicates {
        invoiceId
        status
        totalAmount
        invoiceNo
        createdAt
      }
    }
  }
`;
